import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Scales, Barbell, Sparkle, Leaf, Shield, Heart, PaintBrush, Brain, Flask,
} from "@phosphor-icons/react";
import { usePageMeta } from "../lib/usePageMeta";
import { api } from "../lib/api";
import { RESEARCH_AREAS, bucketProducts } from "../lib/research-areas";

const AREA_ICONS = {
  "weight-management":         Scales,
  "recovery-repair":           Sparkle,
  "growth-performance":        Barbell,
  "healthy-aging-longevity":   Leaf,
  "immune-support":            Shield,
  "hormone-sexual-wellness":   Heart,
  "skin-hair":                 PaintBrush,
  "focus-cognitive-research":  Brain,
  "other-research-compounds":  Flask,
};

/**
 * Plain-English "Shop by research area" landing page.
 *
 * Mirrors the homepage grid but with roomier cards, an explicit product
 * count, and a persistent "Browse the full catalog" fallback so every
 * sellable SKU stays one click away from any entry point.
 */
export default function Categories() {
  usePageMeta({
    title: "Shop by research area",
    description:
      "Browse research peptides by topic: weight management, recovery, growth, healthy aging, immune, hormone, skin & hair, and cognitive research areas.",
    canonicalPath: "/categories",
  });

  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { limit: 200 } })
      .then((r) => setProducts(r.data.items || []))
      .catch(() => setProducts([]));
  }, []);

  const buckets = useMemo(() => bucketProducts(products), [products]);

  return (
    <div className="bg-white" data-testid="categories-page">
      <section className="bg-brand-surface border-b border-brand-border">
        <div className="mps-container py-14 md:py-20">
          <div className="max-w-3xl">
            <div className="eyebrow mb-4">Shop by research area</div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-tight">
              Find what you’re looking for
            </h1>
            <p className="mt-5 text-lg text-brand-muted leading-relaxed max-w-2xl">
              Browse by a familiar research topic, or view the full catalog.
            </p>
          </div>
        </div>
      </section>

      <div className="mps-container py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
          {RESEARCH_AREAS.map((area) => {
            const Icon = AREA_ICONS[area.id] || Flask;
            const count = buckets.get(area.id)?.length || 0;
            return (
              <Link
                key={area.id}
                to={`/shop?area=${area.id}`}
                data-testid={`categories-card-${area.id}`}
                className="group flex flex-col gap-6 rounded-[22px] bg-white border border-brand-border p-7 lg:p-8 shadow-[0_2px_10px_rgba(10,35,66,0.05)] hover:shadow-[0_18px_44px_rgba(10,35,66,0.12)] hover:-translate-y-[3px] hover:border-brand-accent/40 transition-[transform,box-shadow,border-color] duration-200"
              >
                <div className="h-14 w-14 rounded-[16px] bg-gradient-to-br from-brand-accent/10 to-brand-accent/20 border border-brand-accent/20 flex items-center justify-center group-hover:from-brand-accent group-hover:to-brand-accent group-hover:border-brand-accent transition-colors duration-200">
                  <Icon size={24} weight="regular" className="text-brand-accent group-hover:text-white transition-colors duration-200" aria-hidden />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-brand-navy leading-tight tracking-tight">
                    {area.label}
                  </h3>
                  <p className="mt-3 text-sm text-brand-muted leading-relaxed">
                    {area.description}
                  </p>
                  {area.examples && (
                    <p className="mt-3 text-[12px] text-brand-muted">
                      e.g. {area.examples}
                    </p>
                  )}
                </div>
                <div className="pt-4 flex items-center justify-between border-t border-brand-border">
                  <span className="text-[12px] text-brand-muted">
                    {count > 0 ? `${count} peptide${count === 1 ? "" : "s"}` : "Browse group"}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-accent group-hover:text-brand-navy transition-colors">
                    Shop {area.label.split(/[\s&]/)[0]} <ArrowRight size={13} weight="bold" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/shop"
            data-testid="categories-browse-all"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-[12px] bg-brand-navy hover:bg-brand-navy2 text-white font-semibold text-sm transition-colors"
          >
            Browse the full catalog <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}
